import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Users, Bike, CalendarDays, MapPin } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/admin/stats")({
  component: AdminStats,
});

interface Stats {
  riders: number; active: number; week: number; topCity: string;
  byDay: { day: string; rides: number }[];
}

function AdminStats() {
  const [s, setS] = useState<Stats | null>(null);

  useEffect(() => {
    (async () => {
      const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
      const [{ count: riders }, { count: active }, { count: week }, { data: pts }, { data: weekRides }] = await Promise.all([
        supabase.from("profiles").select("*", { count: "exact", head: true }),
        supabase.from("rides").select("*", { count: "exact", head: true }).eq("status", "active"),
        supabase.from("rides").select("*", { count: "exact", head: true }).gte("created_at", weekAgo.toISOString()),
        supabase.from("departure_points").select("city"),
        supabase.from("rides").select("created_at").gte("created_at", weekAgo.toISOString()),
      ]);

      const cityCounts: Record<string, number> = {};
      (pts || []).forEach((p: any) => { cityCounts[p.city] = (cityCounts[p.city] || 0) + 1; });
      const topCity = Object.entries(cityCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "—";

      const byDay: { day: string; rides: number }[] = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(); d.setDate(d.getDate() - i); d.setHours(0,0,0,0);
        const label = d.toLocaleDateString("es-ES", { weekday: "short" });
        const next = new Date(d); next.setDate(next.getDate() + 1);
        const count = (weekRides || []).filter((r: any) => {
          const t = new Date(r.created_at).getTime();
          return t >= d.getTime() && t < next.getTime();
        }).length;
        byDay.push({ day: label, rides: count });
      }

      setS({
        riders: riders ?? 0,
        active: active ?? 0,
        week: week ?? 0,
        topCity,
        byDay,
      });
    })();
  }, []);

  return (
    <div>
      <h1 className="font-display text-3xl">Stats</h1>
      <p className="text-sm text-muted-foreground">Pulso de la comunidad.</p>

      {!s ? (
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          {[0,1,2,3].map((i) => <div key={i} className="h-24 rounded-2xl bg-surface-elevated animate-pulse" />)}
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Metric Icon={Users} label="Total riders" value={s.riders} />
            <Metric Icon={Bike} label="Salidas activas" value={s.active} />
            <Metric Icon={CalendarDays} label="Creadas esta semana" value={s.week} />
            <Metric Icon={MapPin} label="Ciudad más activa" value={s.topCity} />
          </div>

          <div className="mt-6 rounded-2xl border border-border bg-surface p-4">
            <h3 className="mb-3 text-xs uppercase tracking-wider text-muted-foreground">Salidas por día (últimos 7)</h3>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={s.byDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis dataKey="day" stroke="#888" fontSize={11} />
                  <YAxis stroke="#888" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    cursor={{ fill: "rgba(245, 197, 24,0.08)" }}
                    contentStyle={{ background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 12, fontSize: 12 }}
                  />
                  <Bar dataKey="rides" fill="#F5C518" radius={[6,6,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ Icon, label, value }: { Icon: any; label: string; value: number | string }) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-4">
      <Icon className="h-5 w-5 text-primary" strokeWidth={1.8} />
      <div className="mt-2 font-display text-3xl text-primary">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}
